import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Mail } from "lucide-react";
import { SUPPORT_EMAIL } from "@/lib/api";

export const Logo = ({ className = "", size = "h-11 w-11" }) => (
  <Link to="/" data-testid="site-logo" className={`flex items-center gap-3 font-serif-display text-2xl tracking-tight text-stone-900 ${className}`}>
    <img src="/namonics-logo-sm.png" alt="Namonics logo" className={`${size} rounded-full`} />
    <span>Namonics<span className="text-[#E8820C] text-base align-top">™</span></span>
  </Link>
);

export const CtaButton = ({ testId = "explore-name-btn", className = "" }) => (
  <Link
    to="/explore"
    data-testid={testId}
    className={`group inline-flex items-center gap-3 rounded-full bg-stone-900 px-7 py-3.5 text-sm font-medium tracking-wide text-[#FDFBF7] transition-colors duration-300 hover:bg-[#5B2C36] ${className}`}
  >
    Explore Your Name <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
  </Link>
);

const NAV = [
  { to: "/#method", label: "The Method" },
  { to: "/#how", label: "How It Works" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-[#E6DEC8]/70 bg-[#FDFBF7]/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <Logo />
        <nav className="hidden items-center gap-9 md:flex">
          {NAV.map((n) => (
            <a key={n.label} href={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`} className="text-sm text-stone-600 transition-colors hover:text-stone-900">
              {n.label}
            </a>
          ))}
          <CtaButton testId="header-explore-btn" className="!px-5 !py-2.5" />
        </nav>
        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-[#E6DEC8] bg-[#FDFBF7] px-5 py-6 md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-5">
            {NAV.map((n) => (
              <a key={n.label} href={n.to} onClick={() => setOpen(false)} className="text-base text-stone-700">{n.label}</a>
            ))}
            <CtaButton testId="mobile-explore-btn" className="justify-center" />
          </div>
        </div>
      )}
    </header>
  );
};

export const SiteFooter = () => {
  return (
    <footer className="border-t border-[#E6DEC8] bg-[#F6F2EA]">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 md:grid-cols-12">
        <div className="md:col-span-5">
          <Logo className="text-3xl" size="h-20 w-20" />
          <p className="mt-3 font-serif-display text-xl italic text-stone-600">Your Name Has More to Say Than You Think.</p>
          <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="footer-support-email" className="mt-6 inline-flex items-center gap-2 text-sm text-stone-700 hover:text-[#8B5A2B]">
            <Mail className="h-4 w-4" /> Customer Support: {SUPPORT_EMAIL}
          </a>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm text-stone-600 md:col-span-4">
          {[["/receipt", "Find My Order"], ["/faq", "FAQ"], ["/contact", "Contact & Feedback"], ["/privacy", "Privacy Policy"], ["/terms", "Terms & Conditions"], ["/refund", "Refunds & Cancellation"], ["/disclaimer", "Disclaimer"]].map(([to, l]) => (
            <NavLink key={to} to={to} data-testid={`footer-link-${to.slice(1)}`} className="hover:text-stone-900">{l}</NavLink>
          ))}
          <a href="https://birthtimecorrection.com/about-me/" target="_blank" rel="noopener noreferrer" data-testid="footer-link-about-me" className="hover:text-stone-900">About Me</a>
        </div>
      </div>
    </footer>
  );
};

export const PageShell = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-[#FDFBF7] text-stone-900">
    <SiteHeader />
    <main className="flex-1">{children}</main>
    <SiteFooter />
  </div>
);
